import React, { Component } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import {
  getAllPools,
  getSponsorsAndPools,
  addPool,
  editPoolForSponsor,
} from '../../services/api/pool-manager';
import { RegularText, BoldText } from '../common/Text';
import LoadingComponent from '../common/LoadingComponent';

const Card = styled.div`
  display: flex;
  flex-direction: column;
  margin: 12px;
  padding: 22px;
  background: #ffffff;
  border: 1px solid #eceaf3;
  border-radius: 14px;
  box-shadow: 0 6px 20px rgba(75, 46, 131, 0.06);
`;

const SectionTitle = styled(BoldText)`
  margin-bottom: 4px;
`;

const Divider = styled.div`
  height: 1px;
  background: #f1eff7;
  margin: 22px 0;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 18px;
  margin-top: 12px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled(RegularText)`
  color: #7a7391;
  font-size: 0.8rem;
  margin-bottom: 6px;
`;

const Input = styled.input`
  font-family: BrandonText-Medium, Lato, sans-serif;
  padding: 11px 14px;
  border: 1px solid #ddd6ee;
  border-radius: 10px;
  font-size: 0.9rem;
  min-width: 18rem;
  outline: none;
  :focus {
    border-color: #6b4bd6;
  }
`;

const Select = styled.select`
  font-family: BrandonText-Medium, Lato, sans-serif;
  padding: 11px 14px;
  border: 1px solid #ddd6ee;
  border-radius: 10px;
  font-size: 0.9rem;
  min-width: 18rem;
  background: #ffffff;
  outline: none;
  :focus {
    border-color: #6b4bd6;
  }
`;

const Button = styled.button`
  font-family: BrandonText-Medium, Lato, sans-serif;
  font-size: 0.9rem;
  padding: 11px 22px;
  border: none;
  border-radius: 10px;
  color: white;
  background: linear-gradient(135deg, #6b4bd6, #4b2e83);
  box-shadow: 0 6px 14px rgba(75, 46, 131, 0.22);
  cursor: pointer;
  transition: opacity 0.15s ease;
  :hover {
    opacity: 0.9;
  }
  :disabled {
    background: #c5c0d4;
    box-shadow: none;
    cursor: not-allowed;
  }
`;

const HelperText = styled(RegularText)`
  color: #8a84a0;
  margin-top: 8px;
`;

class ManagePoolsPanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      pools: [],
      sponsors: [],
      loading: false,
      // create
      newPoolName: '',
      copyFromPoolId: '',
      creating: false,
      // assign
      assignSponsorId: '',
      assignPoolId: '',
      assigning: false,
    };
  }

  componentDidMount() {
    this.loadLists();
  }

  loadLists = () => {
    this.setState({ loading: true });
    Promise.all([getAllPools(), getSponsorsAndPools(0, 500, null, null)])
      .then(([poolsRes, sponsorsRes]) => {
        const pools =
          poolsRes && poolsRes.message === 'success' ? poolsRes.pools || [] : [];
        const sponsors =
          sponsorsRes && sponsorsRes.message === 'success'
            ? sponsorsRes.sponsors || []
            : [];
        this.setState({ pools, sponsors });
      })
      .catch((err) => {
        toast.error(err.message || 'Failed to load pools/sponsors');
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  };

  onCreatePool = () => {
    const { newPoolName, copyFromPoolId } = this.state;
    const name = newPoolName.trim();
    if (!name) {
      toast.error('Enter a pool name.');
      return;
    }
    this.setState({ creating: true });
    addPool(name, copyFromPoolId || undefined)
      .then((res) => {
        if (res && res.message === 'success') {
          toast.success(`Pool "${name}" created.`);
          this.setState({ newPoolName: '', copyFromPoolId: '' });
          this.loadLists();
          if (this.props.onChanged) {
            this.props.onChanged();
          }
        } else {
          throw new Error((res && res.errorMessage) || 'Failed to create pool');
        }
      })
      .catch((err) => {
        toast.error(err.message || 'Failed to create pool');
      })
      .finally(() => {
        this.setState({ creating: false });
      });
  };

  onAssignPool = () => {
    const { assignSponsorId, assignPoolId } = this.state;
    if (!assignSponsorId || !assignPoolId) {
      toast.error('Select both a sponsor and a pool.');
      return;
    }
    this.setState({ assigning: true });
    editPoolForSponsor(assignPoolId, assignSponsorId)
      .then((res) => {
        if (res && res.message === 'success') {
          toast.success('Sponsor pool updated.');
          this.loadLists();
          if (this.props.onChanged) {
            this.props.onChanged();
          }
        } else {
          throw new Error(
            (res && res.errorMessage) || 'Failed to update sponsor pool'
          );
        }
      })
      .catch((err) => {
        toast.error(err.message || 'Failed to update sponsor pool');
      })
      .finally(() => {
        this.setState({ assigning: false });
      });
  };

  render() {
    const {
      pools,
      sponsors,
      loading,
      newPoolName,
      copyFromPoolId,
      creating,
      assignSponsorId,
      assignPoolId,
      assigning,
    } = this.state;

    return (
      <Card>
        <BoldText fontSize="1.2rem">Manage Pools</BoldText>
        <HelperText>Create a pool, or assign a pool to a sponsor.</HelperText>

        {loading ? (
          <LoadingComponent />
        ) : (
          <React.Fragment>
            <Divider />
            <SectionTitle>Create a new pool</SectionTitle>
            <Row>
              <Field>
                <Label>Pool name</Label>
                <Input
                  value={newPoolName}
                  placeholder="e.g. Acme Corp Pool"
                  onChange={(e) =>
                    this.setState({ newPoolName: e.target.value })
                  }
                />
              </Field>
              <Field>
                <Label>Copy doctors from (optional)</Label>
                <Select
                  value={copyFromPoolId}
                  onChange={(e) =>
                    this.setState({ copyFromPoolId: e.target.value })
                  }
                >
                  <option value="">Start empty</option>
                  {pools.map((p) => (
                    <option key={p.poolId} value={p.poolId}>
                      {p.poolName} ({p.poolId})
                    </option>
                  ))}
                </Select>
              </Field>
              <Button disabled={creating} onClick={this.onCreatePool}>
                {creating ? 'Creating…' : 'Create pool'}
              </Button>
            </Row>

            <Divider />
            <SectionTitle>Assign pool to sponsor</SectionTitle>
            <Row>
              <Field>
                <Label>Sponsor</Label>
                <Select
                  value={assignSponsorId}
                  onChange={(e) =>
                    this.setState({ assignSponsorId: e.target.value })
                  }
                >
                  <option value="">Select sponsor…</option>
                  {sponsors.map((s) => (
                    <option key={s.sponsorId} value={s.sponsorId}>
                      {s.sponsorName} ({s.sponsorId})
                    </option>
                  ))}
                </Select>
              </Field>
              <Field>
                <Label>Pool</Label>
                <Select
                  value={assignPoolId}
                  onChange={(e) =>
                    this.setState({ assignPoolId: e.target.value })
                  }
                >
                  <option value="">Select pool…</option>
                  {pools.map((p) => (
                    <option key={p.poolId} value={p.poolId}>
                      {p.poolName} ({p.poolId})
                    </option>
                  ))}
                </Select>
              </Field>
              <Button disabled={assigning} onClick={this.onAssignPool}>
                {assigning ? 'Updating…' : 'Update sponsor pool'}
              </Button>
            </Row>
          </React.Fragment>
        )}
      </Card>
    );
  }
}

export default ManagePoolsPanel;
